import type { ReactNode } from "react";

import { cn } from "@/shared/lib/utils";

interface DiagramFrameProps {
	/** Optional small-caps eyebrow above the diagram (e.g. "Data flow"). */
	eyebrow?: string;
	/** Optional title under the eyebrow. */
	title?: string;
	/** The diagram itself (inline SVG / flexbox composition). */
	children: ReactNode;
	/** Optional figure caption rendered below the diagram. */
	caption?: ReactNode;
	className?: string;
}

/**
 * A framed `<figure>` container for an architecture or data-flow diagram — the
 * flat warm "canvas" the inline diagrams live on. Gives diagrams a subtly inset
 * surface (a faint lavender wash distinct from the page) so the boxes read as a
 * designed exhibit rather than loose markup, with an optional eyebrow/title and a
 * `<figcaption>`. This is the kind of framed exhibit markdown cannot express.
 */
export function DiagramFrame({ eyebrow, title, children, caption, className }: DiagramFrameProps) {
	return (
		// Flat warm exhibit: the brand `--card` fill, a warm hairline frame, the 20px
		// brand radius, and NO shadow — the resources-hub surface treatment.
		<figure className={cn("overflow-hidden rounded-2xl border border-border bg-card", className)}>
			{(eyebrow || title) && (
				<div className="border-b border-border px-5 py-4 sm:px-6">
					{eyebrow && (
						<div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
							<span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
							{eyebrow}
						</div>
					)}
					{title && (
						<div className={cn("text-lg font-semibold tracking-tight text-foreground", eyebrow && "mt-2")}>{title}</div>
					)}
				</div>
			)}
			{/* Inset canvas: a faint lavender wash distinct from the card surface, so the
			    diagram sits on its own surface. Scrolls horizontally on narrow viewports
			    rather than squashing the boxes. */}
			<div className="overflow-x-auto bg-accent/30 p-5 sm:p-8">{children}</div>
			{caption && (
				<figcaption className="border-t border-border px-5 py-3 text-sm leading-relaxed text-muted-foreground sm:px-6">
					{caption}
				</figcaption>
			)}
		</figure>
	);
}
